/**
 * The route memory and the one gate that reads it (src/lib/routeMemory.ts).
 *
 * RouteMemory records the page on screen while it renders, so the gate below
 * it in the same tree already knows which page the reader came from. Both are
 * mounted once, in App.tsx, above the route table.
 *
 * FlyAppGate stands in front of the routes: a step from /app into the older
 * /fly-app surface renders the account home instead, and the /fly-app page
 * never mounts (Anand, 24 Sep 26). Every other address, including /fly-app
 * opened directly or reached from anywhere else, renders exactly as before.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { previousRoute, rememberRoute, shouldGateFlyApp } from '@/lib/routeMemory';

/** Keeps this tab's current and previous pathname. Renders nothing. */
export function RouteMemory() {
  const { pathname } = useLocation();
  // While rendering, not after: the gate is drawn in the same pass and has to
  // see the step that is happening now. rememberRoute does nothing when the
  // path has not changed, so a repeated render costs nothing.
  rememberRoute(pathname);
  return null;
}

export function FlyAppGate({ children, agencyDomain = false }: { children?: ReactNode; agencyDomain?: boolean }) {
  const { pathname } = useLocation();
  if (shouldGateFlyApp(previousRoute(), pathname, agencyDomain)) return <Navigate to="/app" replace />;
  return <>{children}</>;
}
